import React, {memo, useEffect, useRef, useState} from 'react';
import Image from 'next/image';
import Link from 'next/link';
import parse, {Element, attributesToProps, domToReact} from 'html-react-parser';
import type {DOMNode} from 'html-react-parser';
import Container from '@/components/atoms/Container';
import SectionHeader from '@/components/molecules/SectionHeader';
import Eyebrow from '@/components/atoms/Eyebrow';
import Icon from '@/components/atoms/Icon';
import ArrowButton from '@/components/atoms/ArrowButton';
import type {IndustryService, IndustryTab} from '@/components/Ui-components/home/HomeIndustryTabs';
import {useAutoplayAllowed} from '@/hooks/useAutoplayAllowed';
import styles from './HomeIndustryTabs.module.scss';

export type {IndustryService, IndustryTab};

// `overlay` drops the numbered badges, the white glass card and the "Know more"
// button: the copy sits straight on the artwork with a progress bar and counter.
// `card` sits on white: heading left / subheading right, a navy rail of icon tabs, and a
// white card with the copy and chips beside the image.
// `industry` keeps the `card` header but on a pale rail: the image leads the white card, then the
// copy and dotted solution chips.
type IndustryTabsVariant = 'default' | 'overlay' | 'card' | 'industry';

interface HomeIndustryTabsProps {
    eyebrow?: string;
    heading: string;
    subheading: string;
    data: IndustryTab[];
    variant?: IndustryTabsVariant;
    /** Header alignment. Defaults to left for `overlay`, centre otherwise. */
    headerAlign?: 'left' | 'center';
    /** `gradient` swaps the variant's flat section ground for the pale blue GlanceStats band. */
    background?: 'default' | 'gradient';
    /**
     * Steps through the tabs on its own (desktop only, on screen, not for reduced motion — see
     * `useAutoplayAllowed`). Hover pauses it; picking a tab stops it for good.
     */
    autoplay?: boolean;
    /** Resting the mouse on a tab opens it (touch and keyboard keep tap/click). */
    hoverSelect?: boolean;
    /** From lg, let the heading run the full container width (one line) instead of the standard 64rem. */
    wideHeading?: boolean;
}

// How long each tab stays up while autoplaying.
const AUTOPLAY_INTERVAL_MS = 5000;
// Hover intent: the pointer must rest on a tab this long before it opens, so sweeping across the
// list doesn't flick through every panel on the way (same delay as useHoverAccordion).
const HOVER_SELECT_DELAY_MS = 120;

const cx = (...names: Array<string | false | undefined>) => names.filter(Boolean).join(' ');

// Small ↗ for linked chips, drawn on a 12px grid so it renders crisp at chip size (a scaled 24px icon blurs).
const ChipArrow = () => (
    <svg className={styles.chipArrow} width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M3.5 8.5 8.5 3.5M4.5 3.5h4v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// `card` and `overlay` chips: an inline link inside a chip's label gets the same trailing ↗ as a whole-chip link.
const withChipArrow = (node: DOMNode) => {
    if (!(node instanceof Element) || node.name !== 'a') return undefined;
    return (
        <a {...attributesToProps(node.attribs)}>
            {domToReact(node.children as DOMNode[])}
            <ChipArrow />
        </a>
    );
};

// `card` variant artwork: intrinsic size of the service images (990 × 1000).
const CARD_IMAGE_WIDTH = 990;
const CARD_IMAGE_HEIGHT = 1000;

// `industry` variant artwork: intrinsic size of the industry images (794 × 1016).
const INDUSTRY_IMAGE_WIDTH = 794;
const INDUSTRY_IMAGE_HEIGHT = 1016;

// Phones lay the tabs and panels out as one list (see the `down(md)` block in the stylesheet): tab i takes
// order 2i and its panel 2i + 1, so the open panel sits under its tab. On desktop both lists keep this
// relative order, so it changes nothing there.
const tabOrder = (index: number): React.CSSProperties => ({order: index * 2});
const panelOrder = (index: number): React.CSSProperties => ({order: index * 2 + 1});

// A raw path with a space would silently break the CSS `url()`; CDN URLs arrive already encoded.
const iconUrl = (icon: NonNullable<IndustryTab['icon']>) => {
    const src = typeof icon === 'string' ? icon : icon.src;
    return src.includes(' ') ? encodeURI(src) : src;
};

const HomeIndustryTabs = ({
    eyebrow,
    heading,
    subheading,
    data,
    variant = 'default',
    headerAlign,
    background = 'default',
    autoplay = false,
    hoverSelect = false,
    wideHeading = false
}: HomeIndustryTabsProps) => {
    const [activeIndex, setActiveIndex] = useState(0);

    // Autoplay. Desktop only (the hook's default): on phones the panel opens under its tab, so
    // advancing would move the page without input (CLS). On desktop the panel area has a fixed
    // height, so switching never shifts anything below.
    const sectionRef = useRef<HTMLDivElement>(null);
    const tabsRowRef = useRef<HTMLDivElement>(null);
    const autoplayAllowed = useAutoplayAllowed(sectionRef);
    const [stopped, setStopped] = useState(false);
    const [hovered, setHovered] = useState(false);
    const playing = autoplay && autoplayAllowed && !stopped && !hovered && data.length > 1;

    useEffect(() => {
        if (!playing) return undefined;
        const timer = setTimeout(() => setActiveIndex((index) => (index + 1) % data.length), AUTOPLAY_INTERVAL_MS);
        return () => clearTimeout(timer);
    }, [playing, activeIndex, data.length]);

    // The tab list scrolls inside its fixed-height rail: keep the active tab in view while
    // autoplaying. Only the rail scrolls, never the page.
    useEffect(() => {
        const rail = tabsRowRef.current;
        if (!autoplay || !rail || rail.scrollHeight <= rail.clientHeight) return;
        const tab = rail.querySelectorAll<HTMLElement>('button')[activeIndex];
        if (!tab) return;
        const top = tab.offsetTop - rail.offsetTop;
        if (top < rail.scrollTop || top + tab.offsetHeight > rail.scrollTop + rail.clientHeight) {
            rail.scrollTo({top: top - (rail.clientHeight - tab.offsetHeight) / 2, behavior: 'smooth'});
        }
    }, [activeIndex, autoplay]);

    // Choosing a tab hands control to the reader.
    const selectTab = (index: number) => {
        setStopped(true);
        setActiveIndex(index);
    };

    // Hover preview: opens the tab without stopping autoplay (hovering already pauses it, and it
    // resumes from this tab once the pointer leaves). Mouse only — a tap is a click.
    const hoverTimer = useRef<ReturnType<typeof setTimeout>>();
    useEffect(() => () => clearTimeout(hoverTimer.current), []);
    const hoverProps = (index: number) =>
        hoverSelect
            ? {
                  onPointerEnter: (event: React.PointerEvent) => {
                      if (event.pointerType !== 'mouse') return;
                      clearTimeout(hoverTimer.current);
                      hoverTimer.current = setTimeout(() => setActiveIndex(index), HOVER_SELECT_DELAY_MS);
                  },
                  onPointerLeave: () => clearTimeout(hoverTimer.current)
              }
            : {};
    const isOverlay = variant === 'overlay';
    const isCard = variant === 'card';
    const isIndustry = variant === 'industry';
    // Both share the split header, white section and tile-style tabs.
    const isCardLike = isCard || isIndustry;

    const renderOverlayPanel = (item: IndustryTab, index: number) => (
        <div className={cx(styles.panel, styles.overlayPanel, activeIndex !== index && styles.hidden)} style={panelOrder(index)}>
            <div className={styles.imageWrap}>
                <Image src={item.image} alt={item.imageAlt} fill quality={90} sizes="(max-width: 900px) 100vw, 1200px" />
            </div>
            <div className={styles.overlayScrim} />

            <div className={styles.overlayContent}>
                <h3 className={cx(styles.overlayTitle, 'font-headers')}>{item.title}</h3>
                <p className={cx(styles.overlayDesc, 'font-regular-text')}>{parse(item.desc)}</p>

                <div className={styles.overlayChipRow}>
                    {item.services.map((service) => (
                        <span key={service.heading} className={cx(styles.overlayChip, 'font-regular-text')}>
                            {parse(service.heading, {replace: withChipArrow})}
                        </span>
                    ))}
                </div>

                <div className={styles.overlayFooter}>
                    <div className={styles.overlayTrack}>
                        <div className={styles.overlayFill} style={{width: `${((index + 1) / data.length) * 100}%`}} />
                    </div>
                    <span className={cx(styles.overlayCounter, 'font-headers')}>
                        {String(index + 1).padStart(2, '0')} / {String(data.length).padStart(2, '0')}
                    </span>
                </div>
            </div>
        </div>
    );

    const renderDefaultPanel = (item: IndustryTab, index: number) => (
        <div className={cx(styles.panel, activeIndex !== index && styles.hidden)} style={panelOrder(index)}>
            <div className={styles.imageWrap}>
                <Image src={item.image} alt={item.imageAlt} fill quality={90} sizes="(max-width: 900px) 100vw, 1200px" />
            </div>

            <div className={styles.glassCard}>
                <h3 className={cx(styles.industryTitle, 'font-headers')}>{item.title}</h3>
                <p className={cx(styles.desc, 'font-regular-text')}>{parse(item.desc)}</p>

                <div className={styles.chipGrid}>
                    {item.services.map((service) =>
                        service.redirect ? (
                            <div className={styles.chipItem} key={service.heading}>
                                <Link href={service.redirect} target="_blank" className="remove-underline" style={{textDecoration: 'none'}}>
                                    <span className={cx(styles.chip, 'font-regular-text')}>{service.heading}</span>
                                </Link>
                            </div>
                        ) : (
                            <div className={styles.chipItem} key={service.heading}>
                                <span className={cx(styles.chipStatic, 'font-regular-text')}>{service.heading}</span>
                            </div>
                        )
                    )}
                </div>

                {item.link && (
                    <ArrowButton variant="navy" iconSize="sm" href={item.link} target="_blank" className={styles.ctaBtn}>
                        {item.ctaText ?? 'Know more'}
                    </ArrowButton>
                )}
            </div>
        </div>
    );

    const renderCardPanel = (item: IndustryTab, index: number) => (
        <div className={cx(styles.cardPanel, activeIndex !== index && styles.hidden)} style={panelOrder(index)}>
            <div className={styles.cardPanelBody}>
                <h3 className={cx(styles.cardTitle, 'font-headers')}>{item.title}</h3>
                <p className={cx(styles.cardDesc, 'font-regular-text')}>{parse(item.desc)}</p>
                <ul className={styles.cardChipList}>
                    {item.services.map((service) => (
                        <li key={service.heading} className={cx(styles.cardChip, 'font-headers')}>
                            {/* Linked chips end with a small ↗ so they read as navigation; plain chips stay as they are. */}
                            {service.redirect ? (
                                <Link href={service.redirect} target="_blank" className={styles.cardChipLink}>
                                    {service.heading}
                                    <ChipArrow />
                                </Link>
                            ) : (
                                // A pill with no single page may carry inline links of its own (e.g. "iOS & Android").
                                parse(service.heading, {replace: withChipArrow})
                            )}
                        </li>
                    ))}
                </ul>
            </div>
            <div className={styles.cardImageWrap}>
                <Image
                    src={item.image}
                    alt={item.imageAlt}
                    width={CARD_IMAGE_WIDTH}
                    height={CARD_IMAGE_HEIGHT}
                    sizes="(max-width: 900px) 90vw, 360px"
                    className={styles.cardImage}
                />
            </div>
        </div>
    );

    const renderIndustryPanel = (item: IndustryTab, index: number) => (
        <div className={cx(styles.industryPanel, activeIndex !== index && styles.hidden)} style={panelOrder(index)}>
            <div className={styles.industryImageWrap}>
                <Image
                    src={item.image}
                    alt={item.imageAlt}
                    width={INDUSTRY_IMAGE_WIDTH}
                    height={INDUSTRY_IMAGE_HEIGHT}
                    sizes="(max-width: 900px) 90vw, 380px"
                    className={styles.industryImage}
                />
            </div>
            <div className={styles.industryBody}>
                <h3 className={cx(styles.industryPanelTitle, 'font-headers')}>{item.title}</h3>
                <p className={cx(styles.industryDesc, 'font-regular-text')}>{parse(item.desc)}</p>
                <ul className={styles.industryChipList}>
                    {item.services.map((service) => (
                        <li key={service.heading} className={cx(styles.industryChip, 'font-regular-text')}>
                            {service.redirect ? (
                                <Link href={service.redirect} target="_blank" className={styles.cardChipLink}>
                                    {service.heading}
                                </Link>
                            ) : (
                                // A pill with no single page may carry inline links of its own (e.g. "iOS & Android").
                                parse(service.heading)
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );

    const renderPanel = isIndustry ? renderIndustryPanel : isCard ? renderCardPanel : isOverlay ? renderOverlayPanel : renderDefaultPanel;

    return (
        // The spacing classes live on the section root, not on a wrapper: the root paints
        // the section's own background, so padding on an outer element would only add page
        // background around the band and leave the content flush with its edges.
        <div
            ref={sectionRef}
            className={cx(
                'top-space bottom-spacing mobile-top-padding mobile-bottom-padding',
                styles.section,
                isOverlay && styles.overlaySection,
                isCardLike && styles.cardSection,
                background === 'gradient' && styles.gradientSection
            )}
        >
            <Container>
                <SectionHeader
                    eyebrow={eyebrow ? <Eyebrow text={eyebrow} onDark={!isOverlay} /> : eyebrow}
                    heading={heading ? parse(heading) : ''}
                    subheading={subheading ? parse(subheading) : ''}
                    align={headerAlign ?? (isOverlay || isCardLike ? 'left' : 'center')}
                    className={cx(isCardLike && styles.cardHeader, wideHeading && styles.wideHeader)}
                    headingClassName={isCardLike ? styles.cardHeading : isOverlay ? styles.overlayHeading : styles.heading}
                    subheadingClassName={cx(
                        isCardLike ? styles.cardSubheading : isOverlay ? styles.overlaySubheading : styles.subheading,
                        wideHeading && styles.wideHeaderSub
                    )}
                />

                <div
                    className={cx(
                        styles.layoutRow,
                        isOverlay && styles.overlayLayoutRow,
                        isCard && styles.cardLayoutRow,
                        isIndustry && styles.industryLayoutRow
                    )}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                >
                    <div
                        ref={tabsRowRef}
                        className={cx(
                            styles.tabsRow,
                            isOverlay && styles.overlayTabsRow,
                            isCard && styles.cardTabsRow,
                            isIndustry && styles.industryTabsRow
                        )}
                    >
                        {data.map((item, index) => {
                            const isActive = activeIndex === index;
                            return (
                                <button
                                    key={item.title}
                                    type="button"
                                    aria-pressed={isActive}
                                    onClick={() => selectTab(index)}
                                    {...hoverProps(index)}
                                    style={tabOrder(index)}
                                    className={cx(
                                        styles.tab,
                                        isActive && styles.tabActive,
                                        isOverlay && styles.overlayTab,
                                        isOverlay && isActive && styles.overlayTabActive,
                                        isCard && styles.cardTab,
                                        isCard && isActive && styles.cardTabActive,
                                        isIndustry && styles.industryTab,
                                        isIndustry && isActive && styles.industryTabActive,
                                        'font-headers'
                                    )}
                                >
                                    {(isCard || (isIndustry && item.icon)) && (
                                        <span
                                            className={cx(styles.cardTabIconTile, isIndustry && styles.industryTabIconTile)}
                                            aria-hidden="true"
                                        >
                                            {item.icon && (
                                                <span
                                                    className={styles.cardTabIcon}
                                                    style={{'--tab-icon': `url("${iconUrl(item.icon)}")`} as React.CSSProperties}
                                                />
                                            )}
                                        </span>
                                    )}
                                    {!isOverlay && !isCardLike && (
                                        <span className={cx(styles.tabNumber, isActive && styles.tabNumberActive)}>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>
                                    )}
                                    {isCardLike ? <span className={styles.cardTabLabel}>{item.title}</span> : item.title}
                                    {isCard && isActive && (
                                        <span className={styles.cardTabChevron} aria-hidden="true">
                                            <Icon name="ArrowForwardIos" />
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* All panels are rendered once, so every industry's copy and service links sit in the
                        server HTML for crawlers; only the active one is shown. On phones the stylesheet
                        dissolves this wrapper and the rail so the open panel lands under its tab. */}
                    {/* With autoplay the panels share one grid cell (see .panelStack), so the area is always as tall
                        as the tallest panel and the timer never moves the page below. */}
                    <div className={cx(styles.desktopPanelWrap, autoplay && styles.panelStack)}>
                        {data.map((item, index) => (
                            <React.Fragment key={item.title}>{renderPanel(item, index)}</React.Fragment>
                        ))}
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default memo(HomeIndustryTabs);
